import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X,
  Download,
  Sparkles,
  BadgeCheck,
  Maximize2,
  Minimize2,
} from "lucide-react";
import LoadingSpinner from "../../../../../common/LoadingSpinner";
import { inspectionsAPI } from "../../../../../../services/api";
import ReportPhotoPicker from "./ReportPhotoPicker";
import ReportRowsEditor from "./ReportRowsEditor";
import ReportPdfPagesPicker from "./ReportPdfPagesPicker";
import RewriteChatModal from "./RewriteChatModal";

const DEFAULT_ROWS = [
  {
    no: "1",
    contractor: "",
    personnel_description: "",
    equipment_description: "",
  },
];

const CommentModeSelect = ({ value, onChange }) => {
  return (
    <div>
      <label className="text-xs text-gray-600">Comments mode</label>
      <select
        value={value || "manual"}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
      >
        <option value="manual">Manual</option>
        <option value="auto_notes">Auto from Inspection Notes</option>
        <option value="auto_notes_findings">Auto from Notes + Findings</option>
      </select>
      <div className="text-xs text-gray-500 mt-1">
        Auto modes are generated on the backend using inspection notes and
        findings (deterministic).
      </div>
    </div>
  );
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const InspectionReportModal = ({
  open,
  onClose,
  projectId,
  inspectionId,
  inspectionNumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [rewriteOpen, setRewriteOpen] = useState(false);

  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [availablePdfDocuments, setAvailablePdfDocuments] = useState([]);
  const [serverReport, setServerReport] = useState(null);

  const [commentMode, setCommentMode] = useState("manual");
  const [manualComment, setManualComment] = useState("");

  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [selectedPdfPages, setSelectedPdfPages] = useState([]);
  const [peRows, setPeRows] = useState(DEFAULT_ROWS);

  const [timeOnSite, setTimeOnSite] = useState("");
  const [timeOffSite, setTimeOffSite] = useState("");

  const [rewriteInstruction, setRewriteInstruction] = useState("");
  const [rewriteInitialText, setRewriteInitialText] = useState("");
  const [rewriteTarget, setRewriteTarget] = useState(null);
  const [maximized, setMaximized] = useState(false);

  const title = useMemo(() => {
    const n = inspectionNumber || inspectionId || "";
    return `Inspection Report • ${n}`;
  }, [inspectionNumber, inspectionId]);

  const resetFromServer = useCallback((ctx) => {
    const rpt = ctx?.report || null;
    setServerReport(rpt);

    setAvailablePhotos(
      Array.isArray(ctx?.available_photos) ? ctx.available_photos : [],
    );
    setAvailablePdfDocuments(
      Array.isArray(ctx?.available_pdf_documents) ? ctx.available_pdf_documents : [],
    );

    setCommentMode(rpt?.comment_mode || "manual");
    setManualComment(rpt?.manual_comment || "");

    setSelectedPhotoIds(
      Array.isArray(rpt?.selected_photo_ids) ? rpt.selected_photo_ids : [],
    );

    setSelectedPdfPages(
      Array.isArray(rpt?.selected_pdf_pages) ? rpt.selected_pdf_pages : [],
    );

    const pr = Array.isArray(rpt?.personnel_rows) ? rpt.personnel_rows : [];
    const er = Array.isArray(rpt?.equipment_rows) ? rpt.equipment_rows : [];
    const n = Math.max(pr.length, er.length, 1);

    const joined = [];
    for (let i = 0; i < n; i++) {
      const p = pr[i] || {};
      const e = er[i] || {};
      joined.push({
        no: (p.no ?? "") || String(i + 1),
        contractor: p.contractor ?? "",
        personnel_description: p.description ?? "",
        equipment_description: e.description ?? "",
      });
    }

    setPeRows(joined.length > 0 ? joined : DEFAULT_ROWS);

    setTimeOnSite("");
    setTimeOffSite("");
  }, []);

  const loadContext = useCallback(async () => {
    if (!projectId || !inspectionId) return;
    setLoading(true);
    try {
      const ctx = await inspectionsAPI.getReportContext(
        projectId,
        inspectionId,
      );
      resetFromServer(ctx || {});
    } catch (err) {
      console.error("Failed to load inspection report context", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, inspectionId, resetFromServer]);

  useEffect(() => {
    if (!open) return;
    loadContext();
  }, [open, loadContext]);

  const payload = useMemo(() => {
    const pr = (peRows || [])
      .map((r, idx) => ({
        no: r.no || String(idx + 1),
        contractor: r.contractor || "",
        description: r.personnel_description || "",
      }))
      .filter((row) => row.no || row.contractor || row.description);

    const er = (peRows || [])
      .map((r) => ({
        description: r.equipment_description || "",
      }))
      .filter((row) => row.description);

    return {
      comment_mode: commentMode || "manual",
      manual_comment: manualComment || null,
      selected_photo_ids: selectedPhotoIds || [],
      selected_pdf_pages: selectedPdfPages || [],
      personnel_rows: pr,
      equipment_rows: er,
    };
  }, [commentMode, manualComment, selectedPhotoIds, selectedPdfPages, peRows]);

  const canRewrite = useMemo(() => {
    return commentMode === "manual" && (manualComment || "").trim().length > 0;
  }, [commentMode, manualComment]);

  const onOpenRewrite = useCallback(() => {
    if (!canRewrite) return;
    setRewriteInitialText(manualComment || "");
    setRewriteInstruction("");
    setRewriteTarget("manual_comment");
    setRewriteOpen(true);
  }, [canRewrite, manualComment]);

  const onApplyRewrite = useCallback(
    (rewritten) => {
      if (!rewriteTarget) return;
      if (rewriteTarget === "manual_comment") {
        setManualComment(rewritten || "");
      }
      setRewriteOpen(false);
    },
    [rewriteTarget],
  );

  const save = useCallback(async () => {
    if (!projectId || !inspectionId) return;
    setSaving(true);
    try {
      if (timeOnSite || timeOffSite) {
        await inspectionsAPI.update(projectId, inspectionId, {
          time_on_site: timeOnSite || null,
          time_off_site: timeOffSite || null,
        });
      }
      await inspectionsAPI.upsertReport(projectId, inspectionId, payload);
      await loadContext();
    } catch (err) {
      console.error("Failed to save inspection report config", err);
    } finally {
      setSaving(false);
    }
  }, [projectId, inspectionId, payload, loadContext, timeOnSite, timeOffSite]);

  const downloadPdf = useCallback(
    async (saveFirst) => {
      if (!projectId || !inspectionId) return;
      setDownloading(true);
      try {
        if (saveFirst) {
          await save();
        } else if (timeOnSite || timeOffSite) {
          await inspectionsAPI.update(projectId, inspectionId, {
            time_on_site: timeOnSite || null,
            time_off_site: timeOffSite || null,
          });
        }

        const res = await inspectionsAPI.downloadReportPdf(
          projectId,
          inspectionId,
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const filename = `inspection-${inspectionNumber || inspectionId}-report.pdf`;
        downloadBlob(blob, filename);
      } catch (err) {
        console.error("Failed to download inspection report PDF", err);
      } finally {
        setDownloading(false);
      }
    },
    [projectId, inspectionId, inspectionNumber, save, timeOnSite, timeOffSite],
  );

  const onSaveAndDownload = useCallback(() => {
    return downloadPdf(true);
  }, [downloadPdf]);

  const filteredPhotos = useMemo(() => {
    return (availablePhotos || []).map((p) => ({
      id: p.id,
      url: p.file_url,
      caption: p.caption || p.file_name,
      inspectionNumber: p.inspection_number,
      uploadedAt: p.upload_date,
    }));
  }, [availablePhotos]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center">
        <div
          className={`bg-white shadow-xl flex flex-col transition-all duration-200
                    ${
                      maximized
                        ? "w-screen h-screen max-w-none max-h-none rounded-none"
                        : "w-full max-w-5xl max-h-[90vh] rounded-xl"
                    }
                `}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex flex-col">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-indigo-500" />
                {title}
              </div>
              <p className="text-xs text-gray-500">
                Configure comments, personnel, equipment and photos, then
                generate a PDF inspection report.
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setMaximized((v) => !v)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                title={maximized ? "Restore" : "Maximize"}
              >
                {maximized ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </button>

              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {(loading || saving || downloading) && (
            <div className="absolute inset-0 bg-white/40 flex items-center justify-center z-50">
              <LoadingSpinner />
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <CommentModeSelect
                  value={commentMode}
                  onChange={setCommentMode}
                />

                <div>
                  <label className="text-xs text-gray-600 flex items-center justify-between">
                    <span>Report comments</span>
                    <button
                      type="button"
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${
                        canRewrite
                          ? "border-indigo-500 text-indigo-600 hover:bg-indigo-50"
                          : "border-gray-200 text-gray-300 cursor-not-allowed"
                      }`}
                      onClick={onOpenRewrite}
                      disabled={!canRewrite}
                    >
                      <Sparkles className="w-3 h-3" />
                      SmokyQ-AI Rewrite
                    </button>
                  </label>
                  <textarea
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm min-h-[140px]"
                    value={manualComment}
                    onChange={(e) => setManualComment(e.target.value)}
                    placeholder={
                      commentMode === "manual"
                        ? "Write your narrative for this inspection report..."
                        : "Comments will be auto-generated based on inspection notes/findings."
                    }
                    disabled={commentMode !== "manual"}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t mt-3">
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600">
                      Time On Site
                    </label>
                    <input
                      type="time"
                      value={timeOnSite}
                      onChange={(e) => setTimeOnSite(e.target.value)}
                      className="mt-1 border rounded-md px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600">
                      Time Off Site
                    </label>
                    <input
                      type="time"
                      value={timeOffSite}
                      onChange={(e) => setTimeOffSite(e.target.value)}
                      className="mt-1 border rounded-md px-3 py-1.5 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <ReportRowsEditor
                  title="Personnel & Equipment"
                  rows={peRows}
                  onChange={setPeRows}
                  columns={[
                    { key: "no", label: "No", width: "48px" },
                    { key: "contractor", label: "Contractor", width: "25%" },
                    {
                      key: "personnel_description",
                      label: "Description (Personnel)",
                    },
                    {
                      key: "equipment_description",
                      label: "Description (Equipment)",
                    },
                  ]}
                />
              </div>
            </div>

            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-gray-800">
                    Photos in report
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Select which inspection photos are embedded into the PDF.
                  </p>
                </div>
              </div>

              <ReportPhotoPicker
                photos={filteredPhotos}
                selectedIds={selectedPhotoIds}
                onChangeSelected={setSelectedPhotoIds}
              />
            </div>

            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-gray-800">
                    PDF pages after photos
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Select pages from PDFs uploaded for this inspection only.
                  </p>
                </div>
              </div>

              <ReportPdfPagesPicker
                availablePdfDocuments={availablePdfDocuments}
                selectedPdfPages={selectedPdfPages}
                onChange={setSelectedPdfPages}
              />
            </div>
          </div>

          <div className="border-t px-4 py-3 flex items-center justify-between">
            <div className="text-[11px] text-gray-500">
              Saved configuration will be reused next time you open this report.
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSaveAndDownload}
                disabled={saving || downloading || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                <Download className="w-3 h-3" />
                Save & Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <RewriteChatModal
        open={rewriteOpen}
        onClose={() => setRewriteOpen(false)}
        initialText={rewriteInitialText}
        instruction={rewriteInstruction}
        onInstructionChange={setRewriteInstruction}
        onApply={onApplyRewrite}
      />
    </>
  );
};

export default InspectionReportModal;
